<template>
  <div class="min-h-screen bg-gray-900">
    <!-- Header -->
    <div class="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-16">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 class="text-4xl font-bold text-white mb-4">{{ $t('attributions.title') }}</h1>
        <p class="text-xl text-gray-300">{{ $t('attributions.subtitle') }}</p>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div class="prose prose-lg max-w-none">
        <!-- Introduction -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('attributions.introduction.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('attributions.introduction.content') }}</p>
        </section>

        <!-- General Disclaimer -->
        <section class="mb-12">
          <div class="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
            <h3 class="text-lg font-semibold text-yellow-900 mb-3">{{ $t('attributions.disclaimer.title') }}</h3>
            <p class="text-yellow-800 mb-2">{{ $t('attributions.disclaimer.content') }}</p>
            <ul class="list-disc pl-6 space-y-2 text-yellow-800 text-sm">
              <li>{{ $t('attributions.disclaimer.typos') }}</li>
              <li>{{ $t('attributions.disclaimer.accuracy') }}</li>
              <li>{{ $t('attributions.disclaimer.contact') }}</li>
            </ul>
          </div>
        </section>

        <!-- Freepik Attribution -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('attributions.freepik.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('attributions.freepik.intro') }}</p>
          
          <div class="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg mb-6">
            <p class="text-blue-800 mb-4">
              <strong>{{ $t('attributions.freepik.disclaimer') }}</strong>
            </p>
            <p class="text-blue-700 text-sm mb-4">
              {{ $t('attributions.freepik.license') }}
            </p>
          </div>

          <!-- Images Section -->
          <div v-if="freepikAssets.images.length > 0" class="mb-8">
            <h3 class="text-xl font-semibold text-gray-200 mb-4">{{ $t('attributions.freepik.images.title') }}</h3>
            <ul class="list-disc pl-6 space-y-3 text-gray-300">
              <li v-for="(asset, index) in freepikAssets.images" :key="index" class="mb-2">
                <strong>{{ asset.name }}:</strong> 
                <a :href="asset.url" target="_blank" rel="noopener noreferrer" class="text-primary underline ml-2">
                  {{ asset.url }}
                </a>
                <span v-if="asset.author" class="text-gray-400 text-sm ml-2">
                  ({{ $t('attributions.freepik.by') }} {{ asset.author }})
                </span>
              </li>
            </ul>
          </div>

          <!-- Videos Section -->
          <div v-if="freepikAssets.videos.length > 0" class="mb-8">
            <h3 class="text-xl font-semibold text-gray-200 mb-4">{{ $t('attributions.freepik.videos.title') }}</h3>
            <ul class="list-disc pl-6 space-y-3 text-gray-300">
              <li v-for="(asset, index) in freepikAssets.videos" :key="index" class="mb-2">
                <strong>{{ asset.name }}:</strong> 
                <a :href="asset.url" target="_blank" rel="noopener noreferrer" class="text-primary underline ml-2">
                  {{ asset.url }}
                </a>
                <span v-if="asset.author" class="text-gray-400 text-sm ml-2">
                  ({{ $t('attributions.freepik.by') }} {{ asset.author }})
                </span>
              </li>
            </ul>
          </div>

          <!-- Vectors/Icons Section -->
          <div v-if="freepikAssets.vectors.length > 0" class="mb-8">
            <h3 class="text-xl font-semibold text-gray-200 mb-4">{{ $t('attributions.freepik.vectors.title') }}</h3>
            <ul class="list-disc pl-6 space-y-3 text-gray-300">
              <li v-for="(asset, index) in freepikAssets.vectors" :key="index" class="mb-2">
                <strong>{{ asset.name }}:</strong> 
                <a :href="asset.url" target="_blank" rel="noopener noreferrer" class="text-primary underline ml-2">
                  {{ asset.url }}
                </a>
                <span v-if="asset.author" class="text-gray-400 text-sm ml-2">
                  ({{ $t('attributions.freepik.by') }} {{ asset.author }})
                </span>
              </li>
            </ul>
          </div>

          <!-- Template for adding new assets (developer reference only, hidden from public view) -->
          <!--
          <div class="bg-gray-800 p-6 rounded-lg border-2 border-dashed border-gray-400 mt-8">
            <h4 class="font-semibold text-gray-200 mb-3">{{ $t('attributions.freepik.template.title') }}</h4>
            <p class="text-gray-300 text-sm mb-4">{{ $t('attributions.freepik.template.instruction') }}</p>
            <pre class="bg-gray-900 p-4 rounded text-xs text-gray-300 overflow-x-auto border border-gray-700"><code>{
  "name": "Asset Name",
  "url": "https://www.freepik.com/free-...",
  "author": "Author Name on Freepik"
}</code></pre>
          </div>
          -->
        </section>

        <!-- Other Attributions -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('attributions.other.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('attributions.other.intro') }}</p>
          
          <div v-if="otherAssets.length > 0" class="space-y-4">
            <div v-for="(asset, index) in otherAssets" :key="index" class="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <h4 class="font-semibold text-gray-200 mb-2">{{ asset.name }}</h4>
              <p class="text-gray-300 text-sm mb-2">{{ asset.description }}</p>
              <a v-if="asset.url" :href="asset.url" target="_blank" rel="noopener noreferrer" class="text-primary underline text-sm">
                {{ asset.url }}
              </a>
            </div>
          </div>
          <p v-else class="text-gray-500 italic">{{ $t('attributions.other.none') }}</p>
        </section>

        <!-- Contact -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('attributions.contact.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('attributions.contact.intro') }}</p>
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <p class="text-gray-300 mb-2"><strong>{{ $t('attributions.contact.email') }}:</strong> connect@heidelbell.com</p>
            <p class="text-gray-300"><strong>{{ $t('attributions.contact.phone') }}:</strong> +49 176 47050093</p>
          </div>
        </section>

        <!-- Last Updated -->
        <section class="mb-12">
          <div class="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p class="text-blue-700">
              <strong>{{ $t('attributions.lastUpdated') }}:</strong> {{ buildDate }}
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBuildDate } from '../composables/useBuildDate'

const { t, locale } = useI18n()
const { buildDate } = useBuildDate()

// SEO Meta tags
document.title = t('attributions.seo.title')
document.querySelector('meta[name="description"]')?.setAttribute('content', t('attributions.seo.description'))

// Freepik assets data structure
// Users can add their Freepik links here
const freepikAssets = ref({
  images: [
    {
      name: 'Person Working with HTML Computer (About Us Section)',
      url: 'https://www.freepik.com/free-photo/person-working-html-computer_XXXXX.htm', // Replace with actual Freepik URL
      author: 'Freepik' // Replace with actual author name from Freepik
    },
    {
      name: 'Vernetzungskonzept Stillleben Arrangement (Our Tech Network)',
      url: 'https://www.freepik.com/free-photo/vernetzungskonzept-stillleben-arrangement_XXXXX.htm', // Replace with actual Freepik URL
      author: 'Freepik' // Replace with actual author name from Freepik
    }
    // Example structure - users should replace with actual Freepik links
    // {
    //   name: 'Background Image 1',
    //   url: 'https://www.freepik.com/free-photo/...',
    //   author: 'Author Name'
    // },
    // {
    //   name: 'Background Image 2',
    //   url: 'https://www.freepik.com/free-photo/...',
    //   author: 'Author Name'
    // }
  ],
  videos: [
    {
      name: 'People Technology Background Video (Hero Section)',
      url: 'https://www.freepik.com/free-video/people-technology_1478243.htm', // Replace with actual Freepik URL if different
      author: 'Freepik' // Replace with actual author name from Freepik
    },
    {
      name: 'People Technology Background Video (About Page)',
      url: 'https://www.freepik.com/free-video/people-technology_1476238.htm', // Replace with actual Freepik URL if different
      author: 'Freepik' // Replace with actual author name from Freepik
    }
  ],
  vectors: [
    // Example structure
    // {
    //   name: 'SVG Vector Icon',
    //   url: 'https://www.freepik.com/free-vector/...',
    //   author: 'Author Name'
    // }
  ]
})

// Other assets from different sources
const otherAssets = computed(() => [])

// Use build date from composable (replaces old dynamic date logic)
</script>

<style scoped>
.prose {
  @apply text-gray-300 leading-relaxed text-justify;
}

.prose h2 {
  @apply text-2xl font-bold text-white mb-4 mt-8;
}

.prose h3 {
  @apply text-xl font-semibold text-gray-200 mb-3 mt-6;
}

.prose .bg-yellow-50 h3 {
  @apply text-yellow-900;
}

.prose h4 {
  @apply text-lg font-semibold text-gray-200 mb-2 mt-4;
}

.prose p {
  @apply mb-4;
}

.prose ul {
  @apply mb-6;
}

.prose li {
  @apply mb-2;
}
</style>
